import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, useHistory, withRouter } from 'react-router-dom';
import { notification, Typography, Drawer } from 'antd';
import { useQuery } from 'react-query';
import DF from 'DisplayFramework';
import DataGrid from 'components/DataGrid';
import ButtonComponent from 'components/Button';
import RouterLink from 'components/RouterLink';
import ParListSelect from 'components/ParListSelect';
import ProductInput from './PropDetail/ProductInput';
import { DataGridProps, DataGridColDef } from 'components/DataGrid/DataGrid';
import { PlusOutlined } from '@ant-design/icons';
import {
  ProposalListResponse,
  findPropByAnyRefOrStatusOrDateRule,
  FindPropByAnyRefOrStatusOrDateParams,
} from 'services/proposal';
import {
  FinCTListResponse,
  findFinCTByAnyRefOrStatusOrDate,
} from 'services/fincontract';
import { getParList, ViewParList } from 'services/parametersService';
import GetLabel from 'DisplayFramework/components/GetLabel';
import SynthesList from 'pages/Configuration/ParSynthesObject/SynthesList';
import ModalControllerNV from 'components/ModalControllerNV';
import ProposalModal from './ProposalModal';
import { isNil } from 'lodash';

type PropListProps = RouteComponentProps & {
  onSelect?: (item: ProposalListResponse) => void;
  hideExtraAction?: boolean;
  hideFilters?: boolean;
  phase?: string;
  mfid?: number;
  tpauthid?: number;
  tpauthdetailid?: number;
  search?: boolean;
  prcode?: string;
  PropOrProd?: string;
  refetch?: boolean;
  hideAction?: boolean;
  clientname?: string;
  setdelet?: any;
  delet?: boolean;
  flagPropWithCtreference?: boolean;
} & Pick<DataGridProps<any>, 'rowSelection'>;

const getLocalTpid = () => {
  const storedValue = localStorage.getItem('tpid');
  const numericValue = Number(storedValue);

  if (Number.isNaN(numericValue)) {
    return 0;
  }

  return numericValue;
};





const PropList = (props: PropListProps) => {
  const search = props.location.search;
  const params = new URLSearchParams(search);

  const phase = params.get('phase');

  const lphase = phase ?? props?.PropOrProd ?? 'PROPOSAL';
  const { onSelect, rowSelection } = props;
  const [listError, setListError] = useState<string | null>(null);
  const localTpid = useMemo(() => getLocalTpid(), []);

  const parseApiError = useCallback((error: any, fallbackMessage: string) => {
    return (
      error?.response?.data?.message ??
      error?.response?.data?.description ??
      error?.message ??
      error?.error_description ??
      fallbackMessage
    );
  }, []);

  const handleMainListError = useCallback(
    (error: any, fallbackMessage: string) => {
      const description = parseApiError(error, fallbackMessage);
      setListError(description);
      notification.error({
        message: fallbackMessage,
        description,
      });
    },
    [parseApiError]
  );

  const initialSearchParams: FindPropByAnyRefOrStatusOrDateParams = {
    branchid: '' as any,
    mfid: props.mfid ?? 0,
    tpauthdetailid: props.tpauthdetailid ?? 0,
    tpauthid: props.tpauthid ?? 0,
    brokername: '',
    clientname: props.clientname ?? '',
    ctphase: props.phase ?? '',
    ctreference: '',
    ctstatus: '',
    datemax: '',
    datemin: '',
    marketingid: '' as any,
    ctnetworkcode: '',
    offerid: '' as any,
    prcode: props.prcode ?? '',
    propreference: '',
    tpidbroker:localStorage.getItem('userrole')==="BROKER"?Number(localStorage.getItem('tpid')) :"" as any,
    tprefbroker:'',
    tpreference: '',
    flagPropWithCtreference: props.flagPropWithCtreference ?? false,
    tpidclient: 0,
    ctexternalpropref: '',
    ctexternalrefde:'',
  };
  const fetchParListSafely = useCallback(
    async (parcode: string) => {
      try {
        const response = await getParList({ parcode, tpid: localTpid });
        return response.list || [];
      } catch (error) {
        const description = parseApiError(
          error,
          'Parameters Service is not available'
        );
        notification.error({
          message: 'Parameters Service is not available',
          description,
        });
        return [];
      }
    },
    [localTpid, parseApiError]
  );

  const history = useHistory();
  const [searchs, setsearchs] = useState<any>(false);
  const [searchParams, setSearchParams] =
    useState<FindPropByAnyRefOrStatusOrDateParams>({
      ...initialSearchParams,
    });

  const [visible, setVisible] = useState(false);

  const [ctrid, setctrid] = useState<number>();
  const [prcode, setprcode] = useState<string>();
  const [currcode, setcurrcode] = useState<string>();
  const closeSynthes = () => {
    setVisible(false);
  };
  const openDrawerSynthes = (ctrid: number, currcode: string) => {
    setctrid(ctrid);
    setVisible(true);
    setcurrcode(currcode);
  };
  const useContracts = (searchParams: any) => {
    const proposalQuery = useQuery(
      ['findPropByAnyRefOrStatusOrDate', searchParams, props.tpauthid],
      async () => {
        const response = await findPropByAnyRefOrStatusOrDateRule({
          ...searchParams,
          tpauthid: props.tpauthid ?? 0,
        });
        return response.list;
      },
      {
        enabled: lphase === 'PROPOSAL' && !isNil(props.search),
        staleTime: 60 * 1000,
        retry: false,
        onError: (error) =>
          handleMainListError(error, 'Erreur'),
      }
    );

    const fincontractQuery = useQuery(
      ['findFinCTByAnyRefOrStatusOrDate', searchParams],
      async () => {
        const response = await findFinCTByAnyRefOrStatusOrDate(searchParams);
        return response.list;
      },
      {
        enabled: lphase === 'PROD' && !isNil(props.search),
        retry: false,
        onError: (error) =>
          handleMainListError(error, 'Contract Service is not available'),
      }
    );

    return lphase === 'PROPOSAL' ? proposalQuery : fincontractQuery;
  };

  const {
    data: dataSource,
    isFetching,
    refetch,
    error,
  } = useContracts(searchParams);

  useEffect(() => {
    if (!error) {
      setListError(null);
    }
  }, [error]);

  const statusParcode = lphase === 'PROD' ? 'CTSTATUS' : 'PROPSTATUS';

  const { data: channelParList = [] } = useQuery<ViewParList[]>(
    ['parList', 'NETWORK', localTpid],
    () => fetchParListSafely('NETWORK'),
    {
      staleTime: 5 * 60 * 1000,
      retry: false,
    }
  );

  const { data: statusParList = [] } = useQuery<ViewParList[]>(
    ['parList', statusParcode, localTpid],
    () => fetchParListSafely(statusParcode),
    {
      staleTime: 5 * 60 * 1000,
      enabled: Boolean(statusParcode),
      retry: false,
    }
  );

  

  useEffect(() => {
    if (props.refetch) refetch();
    if (dataSource && dataSource?.length === 0) {
      props.setdelet(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.refetch, props.setdelet, props.delet, dataSource]);

  const columns: DataGridColDef<
    FinCTListResponse | ProposalListResponse | any
  >[] = useMemo(() => [
    {
      dataIndex: 'ctexternalpropref',
      render: (propreference, record) => {
        if (onSelect) {
          return (
            <Typography.Link onClick={() => onSelect(record)}>
              {propreference}
            </Typography.Link>
          );
        } else if (props.search && props.mfid)
          return (
            <ModalControllerNV
              content={({ setVisible, visible }) => (
                <a
                  onClick={() => {
                    setVisible(true);
                  }}
                >
                  {propreference}
                </a>
              )}
              modal={({ setVisible, visible }) => (
                <ProposalModal
                  ctrid={Number(record.ctrid ?? null)}
                  visible={visible}
                  onCancel={() => setVisible(false)}
                  onSuccess={() => setVisible(false)}
                  title="Master Facility"
                />
              )}
            />
          );
        else
          return (
            <RouterLink
              to={`/proposal/${record.ctrid}?phase=${lphase}`}
              prevent={props.hideExtraAction}
            >
              {propreference}
            </RouterLink>
          );
      },
      filtrable: true,
      
      width: 70,
    },
    {
      dataIndex: 'ctexternalrefde',
      render: (value, record) => record.ctexternalrefde,
      filtrable: true,
      width: 70,
    },

    {
      dataIndex: 'prcode',
      width: 70,
      ellipsis: true,
      filtrable: true,
      filterComponent: (
        <ProductInput
          style={{ width: '100%' }}
          onSelected={(value) => {
            setprcode(value);
          }}
        />
      ),
      render: (value, record) => record.prcodelabel,
    },
    
    {
      comcode: 'ctrid',
      dataIndex: 'ctrid',
      filtrable: true,
      width: 70,
      render: (value, record) => record.ctrid,
    },
    {
      comcode: 'ctnetworkcode',
      dataIndex: 'ctnetworkcode',
      filtrable: true,
      filterComponent: <ParListSelect parcode="NETWORK" allowClear/>,
      render: (value, record) => record.ctnetworklabel,
      width: 70,
    },
    {
      comcode: 'ctstatus',
      dataIndex: 'ctstatus',
      width: 70,
      ellipsis: true,
      filtrable: true,
      filterComponent: (
        <ParListSelect
          parcode={lphase === 'PROD' ? 'CTSTATUS' : 'PROPSTATUS'}
          style={{ width: '100%' }}
          allowClear
        />
      ),
      render: (value, record) => record.statuslabel,
    
    }

  ], [onSelect, props.search, props.mfid, props.hideExtraAction, lphase, openDrawerSynthes]);

  const filterColumns = () => {
    if (props.hideFilters) {
      return columns.filter(
        (e) => e.key !== 'synthes' && e.key !== 'actions' && e.key !== 'copy'
      );
    } else if (props?.PropOrProd === 'PROD') {
      // For PROD phase, hide copy button (only for PROPOSAL)
      return columns.filter(
        (e) =>
          e.key !== 'synthes' &&
          e.key !== 'actions' &&
          e.dataIndex !== 'propreference' &&
          e.key !== 'copy'
      );
    } else if (props.flagPropWithCtreference) {
      // Keep copy column even with flagPropWithCtreference if it's PROPOSAL phase
      return columns.filter(
        (e) =>
          e.dataIndex !== 'propreference' &&
          e.key !== 'synthes' &&
          e.key !== 'actions' &&
          e.dataIndex !== 'propreference'
      );
    } else {
      return columns;
    }
  };

  return (
    <DF.Provider pagcode="proplist">
      
      <DataGrid
        titleComcode={lphase === 'PROD' ? 'pageTitleProd' : 'pageTitleProposal'}
        scroll={{ x: 1500, y: 500 }}
        dataSource={dataSource ?? []}
        rowKey={(record) => record.ctrid}
        columns={filterColumns()}
        onSearch={(values) => {
          /*  if (lphase === 'PROD') {
            setissearchPROD(true);
            setissearchPROPOSAL(false);
          } else {
            setissearchPROPOSAL(true);
            setissearchPROD(false);
          }*/
          setSearchParams({ ...values });
          setsearchs(true);
          refetch();
        }}
        componentName="proposal_list"
        initialSearchValues={initialSearchParams}
        isLoading={isFetching}
        rowSelection={rowSelection}
        extractData="ProposalList"
        hideExtraAction={props.hideExtraAction}
        extraActions={
          <ButtonComponent
            onClick={() => history.push('/proposal/create')}
            icon={<PlusOutlined />}
            type="primary"
            comcode="header.btnnew"
          />
        }
        hideFilters={props.hideFilters}
      />
      <Drawer
        title={<GetLabel label="synthesliste" />}
        width={400}
        onClose={closeSynthes}
        visible={visible}
        destroyOnClose
        headerStyle={{ backgroundColor: '#00a896' }}
        bodyStyle={{ paddingBottom: 80, alignContent: 'center' }}
        style={{
          //position: 'absolute',
          right: '0px',
          bottom: '0px',
          width: '100%',
          padding: '10px 16px',
          //textAlign: 'right',
          //background: '#fff',
          //borderTop: '1px solid #e9e9e9',
        }}
      >
        <SynthesList
          objecttype="PROPOSAL"
          objectid={ctrid || 0}
          currcode={currcode ?? ''}
        />
      </Drawer>
    </DF.Provider>
  );
};

export default withRouter(PropList);
